import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Radar, ExternalLink, TrendingUp, Minus, Loader2, AlertTriangle, Wifi, WifiOff } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const SportLiveEngineModal = ({ open, onOpenChange, onLogin }) => {
  const { user } = useAuth();
  const [phase, setPhase] = useState("connecting");
  const [status, setStatus] = useState(null);
  const [feed, setFeed] = useState([]);
  const [forceDown, setForceDown] = useState(false);

  const connect = useCallback(
    async (down) => {
      setPhase("connecting");
      const force = down ? "unavailable" : undefined;
      try {
        const [{ data: st }, { data: fd }] = await Promise.all([
          api.get("/sport/status", { params: { force } }),
          api.get("/sport/scouting-feed", { params: { force } }),
        ]);
        setStatus(st);
        setFeed(fd.athletes || []);
        setPhase(st.available ? "connected" : "fallback");
      } catch (e) {
        setPhase("fallback");
        setStatus({ available: false, message: "Unable to reach the Talanta Sport engine." });
      }
    },
    []
  );

  useEffect(() => {
    if (open) {
      setForceDown(false);
      const t = setTimeout(() => connect(false), 900);
      return () => clearTimeout(t);
    }
  }, [open, connect]);

  const toggleFallback = () => {
    const next = !forceDown;
    setForceDown(next);
    connect(next);
  };

  const deepLink = async () => {
    if (!user) {
      toast.error("Sign in with One Talanta ID to continue to Talanta Sport");
      onOpenChange(false);
      onLogin();
      return;
    }
    try {
      const { data } = await api.post("/sport/handoff");
      toast.success("Secure handoff created — opening Talanta Sport");
      window.open(data.deep_link, "_blank", "noopener");
    } catch (e) {
      toast.error("Could not create secure handoff");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="sports-engine-modal" className="sm:max-w-lg p-0 overflow-hidden border-0 bg-white">
        <DialogTitle className="sr-only">Talanta Sport live engine</DialogTitle>
        <DialogDescription className="sr-only">Connecting to the NextGen scouting engine.</DialogDescription>
        <div className="kenya-stripe h-1.5" />
        <div className="bg-talanta-green px-6 pt-6 pb-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-talanta-gold text-xs font-semibold uppercase tracking-widest">
                <Radar className="h-4 w-4" /> Talanta Sport
              </div>
              <h2 className="mt-1.5 font-display font-bold text-2xl">NextGen Scouting Engine</h2>
            </div>
            <StatusIndicator phase={phase} status={status} />
          </div>
        </div>

        <div className="px-6 py-6">
          {phase === "connecting" && (
            <div className="flex flex-col items-center py-10">
              <div className="relative h-28 w-28">
                <span className="absolute inset-0 rounded-full border-2 border-talanta-green/15" />
                <span className="absolute inset-0 rounded-full border-t-2 border-talanta-red animate-radar-spin" />
                <span className="absolute inset-3 rounded-full border border-talanta-green/10" />
                <span className="absolute inset-0 flex items-center justify-center">
                  <Radar className="h-8 w-8 text-talanta-green" />
                </span>
                <span className="absolute inset-0 rounded-full bg-talanta-red/20 animate-ping-slow" />
              </div>
              <p className="mt-5 font-display font-semibold text-talanta-green">Connecting to live engine…</p>
              <p className="text-sm text-talanta-charcoal/50">Establishing secure link with NextGen</p>
            </div>
          )}

          {phase === "connected" && (
            <div>
              <div className="flex items-center justify-between">
                <p className="text-sm text-talanta-charcoal/70">{status?.message}</p>
                <span className="text-[10px] uppercase tracking-wider font-semibold rounded-full bg-talanta-green/10 text-talanta-green px-2 py-1">
                  {status?.mode}
                </span>
              </div>
              <div data-testid="live-scout-feed" className="mt-4 space-y-2 max-h-64 overflow-y-auto pr-1">
                {feed.map((a) => (
                  <div key={a.id} className="flex items-center gap-3 rounded-xl border border-talanta-green/10 bg-talanta-cream px-3 py-2.5">
                    <div className="h-9 w-9 rounded-lg bg-talanta-green text-white flex items-center justify-center font-display font-bold text-sm">
                      {a.name.split(" ").map((n) => n[0]).join("")}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-talanta-charcoal text-sm truncate">{a.name}</div>
                      <div className="text-xs text-talanta-charcoal/50">{a.discipline} · {a.county}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-display font-bold text-talanta-green">{a.rating}</div>
                      <div className={`text-[10px] font-semibold inline-flex items-center gap-0.5 ${a.trend === "up" ? "text-emerald-600" : "text-talanta-charcoal/40"}`}>
                        {a.trend === "up" ? <TrendingUp className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
                        {a.trend}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {phase === "fallback" && (
            <div className="flex flex-col items-center py-8 text-center">
              <div className="h-14 w-14 rounded-full bg-talanta-red/10 flex items-center justify-center">
                <AlertTriangle className="h-7 w-7 text-talanta-red" />
              </div>
              <p className="mt-4 font-display font-semibold text-talanta-green">Live engine unavailable</p>
              <p className="mt-1 text-sm text-talanta-charcoal/60 max-w-xs">
                {status?.message || "We couldn't reach Talanta Sport right now."} Your One Talanta ID stays active — try again shortly.
              </p>
              <button onClick={() => connect(forceDown)} className="mt-4 rounded-full bg-talanta-green px-5 py-2 text-white text-sm font-semibold flex items-center gap-2">
                <Loader2 className="h-4 w-4" /> Retry connection
              </button>
            </div>
          )}

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button
              data-testid="deep-link-out-btn"
              onClick={deepLink}
              className="flex-1 rounded-xl bg-talanta-red hover:bg-talanta-red/90 py-3 text-white font-semibold flex items-center justify-center gap-2 transition-all hover:scale-[1.01]"
            >
              Continue to Talanta Sport <ExternalLink className="h-4 w-4" />
            </button>
            <button
              data-testid="simulate-fallback-btn"
              onClick={toggleFallback}
              className="rounded-xl border border-talanta-green/20 px-4 py-3 text-sm font-semibold text-talanta-green hover:bg-talanta-cream flex items-center justify-center gap-2"
            >
              {forceDown ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}
              {forceDown ? "Restore" : "Simulate outage"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const StatusIndicator = ({ phase, status }) => {
  const online = phase === "connected";
  const connecting = phase === "connecting";
  return (
    <div
      data-testid="nextgen-status-indicator"
      className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
        online ? "bg-emerald-500/15 text-emerald-300" : connecting ? "bg-amber-500/15 text-amber-300" : "bg-red-500/15 text-red-300"
      }`}
    >
      <span className={`h-2 w-2 rounded-full ${online ? "bg-emerald-400" : connecting ? "bg-amber-400 animate-pulse" : "bg-red-400"}`} />
      {online ? "NextGen online" : connecting ? "Connecting" : "Offline"}
    </div>
  );
};
